import React, { useState, useEffect } from 'react';
import MultiSelectTicker from '../../MultiSelectTicker';
import {
  SCAN_MODES,
  DEFAULT_MODE,
  getMode,
  modeToFlags,
  EXPIRATION_BUCKETS,
  SORT_OPTIONS,
} from './scalpingModes';

const STORAGE_KEY = 'scalping_filters';

const DEFAULTS = {
  tickers: [],
  mode: DEFAULT_MODE,
  contractType: 'call',
  maxPrice: 0.5,
  minDropPercent: 40,
  expirationRanges: ['1-3m', '3-5m'],
  otmMin: -5,
  otmMax: 25,
  excludeStale: true,
  minVolume: 0,
  sortBy: 'drop_percent',
};

const POPULAR_TICKERS = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'AMD'];

/** Re-running near-identical scans is the norm, so the filter set survives reloads. */
function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const saved = JSON.parse(raw);
    return { ...DEFAULTS, ...saved };
  } catch {
    return DEFAULTS;
  }
}

function ScalpingFilterPanel({ onScan, loading }) {
  const [form, setForm] = useState(loadSavedFilters);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch {
      /* storage full or disabled - not worth interrupting the user over */
    }
  }, [form]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const activeMode = getMode(form.mode);
  const showPrice = activeMode.usePriceFilter;
  const showDrop = activeMode.useDropFilter;

  const toggleBucket = (key) => {
    setForm((prev) => {
      const next = prev.expirationRanges.includes(key)
        ? prev.expirationRanges.filter((k) => k !== key)
        : [...prev.expirationRanges, key];
      return { ...prev, expirationRanges: next };
    });
  };

  const addPopularTicker = (ticker) => {
    if (form.tickers.some((t) => t.value === ticker)) return;
    set({ tickers: [...form.tickers, { label: ticker, value: ticker }] });
  };

  const canScan =
    !loading && form.tickers.length > 0 && form.expirationRanges.length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canScan) return;

    const payload = {
      tickers: form.tickers.map((t) => t.value),
      contract_type: form.contractType,
      ...modeToFlags(form.mode),
      expiration_ranges: form.expirationRanges,
      otm_min: Number(form.otmMin),
      otm_max: Number(form.otmMax),
      exclude_stale: form.excludeStale,
      min_volume: Number(form.minVolume) || 0,
      sort_by: form.sortBy,
    };

    // Only send the inputs the chosen mode actually uses.
    if (showPrice) payload.max_price = Number(form.maxPrice);
    if (showDrop) payload.min_drop_percent = Number(form.minDropPercent);

    onScan(payload);
  };

  const numberInput =
    'w-28 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tickers */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Ticker Symbols *
        </label>
        <MultiSelectTicker
          value={form.tickers}
          onChange={(selected) => set({ tickers: selected || [] })}
          placeholder="Search and select tickers..."
        />
        <div className="mt-3">
          <div className="text-xs text-gray-400 mb-2">Quick Selection:</div>
          <div className="flex flex-wrap gap-2">
            {POPULAR_TICKERS.map((ticker) => (
              <button
                key={ticker}
                type="button"
                onClick={() => addPopularTicker(ticker)}
                disabled={loading}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  form.tickers.some((t) => t.value === ticker)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {ticker}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Up to 10 tickers per scan. One full chain is fetched per ticker.
          </p>
        </div>
      </div>

      {/* Scan mode */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Scan Mode *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {SCAN_MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              onClick={() => set({ mode: mode.key })}
              disabled={loading}
              className={`px-3 py-3 text-sm rounded-md font-medium transition-colors text-left ${
                form.mode === mode.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span className="mr-1">{mode.icon}</span>
              {mode.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2 italic">{activeMode.question}</p>
      </div>

      {/* Mode-dependent inputs */}
      {(showPrice || showDrop) && (
        <div className="flex flex-wrap gap-6 bg-gray-700/30 rounded-lg p-4">
          {showPrice && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max price per contract
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.maxPrice}
                  onChange={(e) => set({ maxPrice: e.target.value })}
                  disabled={loading}
                  className={numberInput}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Caps risk per contract.</p>
            </div>
          )}

          {showDrop && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Min drop from yesterday&apos;s close
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={form.minDropPercent}
                  onChange={(e) => set({ minDropPercent: e.target.value })}
                  disabled={loading}
                  className={numberInput}
                />
                <span className="text-gray-400">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Show contracts down at least this much since the prior close.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Contract type */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Contract Type *
        </label>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => set({ contractType: 'call' })}
            disabled={loading}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              form.contractType === 'call'
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Calls
          </button>
          <button
            type="button"
            onClick={() => set({ contractType: 'put' })}
            disabled={loading}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              form.contractType === 'put'
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Puts
          </button>
        </div>
      </div>

      {/* Expiration buckets - always applied */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Expiration Window * (select one or more)
        </label>
        <div className="flex flex-wrap gap-2">
          {EXPIRATION_BUCKETS.map((bucket) => (
            <button
              key={bucket.key}
              type="button"
              onClick={() => toggleBucket(bucket.key)}
              disabled={loading}
              className={`px-3 py-2 text-sm rounded-md font-medium transition-colors ${
                form.expirationRanges.includes(bucket.key)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {bucket.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Applied in every mode. A discount on something expiring this week is usually
          just theta doing its job — time is what separates a discount from a corpse.
        </p>
      </div>

      {/* Moneyness + liquidity + sort */}
      <div className="flex flex-wrap gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            OTM Range (%)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={form.otmMin}
              onChange={(e) => set({ otmMin: e.target.value })}
              disabled={loading}
              className={numberInput}
            />
            <span className="text-gray-400">to</span>
            <input
              type="number"
              value={form.otmMax}
              onChange={(e) => set({ otmMax: e.target.value })}
              disabled={loading}
              className={numberInput}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Positive is out-of-the-money, for calls and puts alike.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Min Volume
          </label>
          <input
            type="number"
            min="0"
            value={form.minVolume}
            onChange={(e) => set({ minVolume: e.target.value })}
            disabled={loading}
            className={numberInput}
          />
          <p className="text-xs text-gray-500 mt-1">0 keeps untraded contracts.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Sort By
          </label>
          <select
            value={form.sortBy}
            onChange={(e) => set({ sortBy: e.target.value })}
            disabled={loading}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stale toggle */}
      <div>
        <label
          className="flex items-center gap-2 cursor-pointer w-fit"
          title="A price that hasn't updated recently produces a fake discount: the contract looks marked down, but you're comparing against a mark nobody has refreshed."
        >
          <input
            type="checkbox"
            checked={form.excludeStale}
            onChange={(e) => set({ excludeStale: e.target.checked })}
            disabled={loading}
            className="w-4 h-4 accent-blue-500"
          />
          <span className="text-sm text-gray-300">Exclude stale quotes</span>
          <span className="text-xs text-gray-500">(recommended)</span>
        </label>
      </div>

      {/* Submit */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={!canScan}
          className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Scanning chains...
            </div>
          ) : (
            'Run Scan'
          )}
        </button>
        {form.tickers.length === 0 && (
          <p className="text-xs text-gray-500 mt-2">Select at least one ticker.</p>
        )}
        {form.tickers.length > 0 && form.expirationRanges.length === 0 && (
          <p className="text-xs text-gray-500 mt-2">Select at least one expiration window.</p>
        )}
      </div>
    </form>
  );
}

export default ScalpingFilterPanel;
